import { useState } from "react";

/**
 * Modal shown when completing a time ticket for a pending quote (no order/job yet).
 * Gives user the option to enter quantity or just book time with auto-quantity.
 */
export default function PendingQuoteCompleteModal({ ticket, onComplete, onCancel }) {
  const [mode, setMode] = useState(null); // null | 'quantity' | 'auto'
  const [quantity, setQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      if (mode === "quantity") {
        // User entered a quantity
        await onComplete({
          piecesFinished: parseInt(quantity, 10) || 0,
          useAutoQuantity: false,
        });
      } else {
        // Auto-quantity mode - will be calculated on sync
        await onComplete({
          piecesFinished: null,
          useAutoQuantity: true,
        });
      }
    } catch (err) {
      console.error("Error completing pending quote ticket:", err);
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: "#1a1a1a",
          borderRadius: 12,
          padding: 24,
          width: "100%",
          maxWidth: 450,
          border: "1px solid #b45309",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "rgba(245, 158, 11, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              style={{ width: 20, height: 20, color: "#f59e0b" }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: "#fbbf24" }}>
              Complete Pending Quote Work
            </h2>
            <div style={{ fontSize: 12, color: "#a1a1aa", marginTop: 2 }}>
              Quote #{ticket?.quoteNumber || "Unknown"}
            </div>
          </div>
        </div>

        {/* Explanation */}
        <div
          style={{
            padding: 12,
            backgroundColor: "rgba(245, 158, 11, 0.1)",
            border: "1px solid rgba(245, 158, 11, 0.3)",
            borderRadius: 8,
            marginBottom: 20,
            fontSize: 13,
            color: "#fcd34d",
            lineHeight: 1.5,
          }}
        >
          You're completing work on a quote that hasn't been kicked off yet.
          This time will be tracked and can be assigned to a job once the quote has orders.
        </div>

        {/* Ticket Info */}
        <div
          style={{
            padding: 12,
            backgroundColor: "#111",
            borderRadius: 8,
            marginBottom: 20,
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13 }}>
            <div>
              <span style={{ color: "#71717a" }}>Operation:</span>{" "}
              <span style={{ color: "#fff" }}>{ticket?.operationCode || "—"}</span>
            </div>
            <div>
              <span style={{ color: "#71717a" }}>Project:</span>{" "}
              <span style={{ color: "#fff" }}>{ticket?.projectName || ticket?.jobName || "—"}</span>
            </div>
          </div>
        </div>

        {/* Choice Buttons */}
        {!mode && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
            <button
              onClick={() => setMode("quantity")}
              style={{
                padding: "14px 16px",
                backgroundColor: "#1e3a5f",
                border: "1px solid #3b82f6",
                borderRadius: 8,
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "rgba(59, 130, 246, 0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ width: 16, height: 16, color: "#60a5fa" }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                  />
                </svg>
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>Enter Quantity</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                  I know how many pieces I completed
                </div>
              </div>
            </button>

            <button
              onClick={() => setMode("auto")}
              style={{
                padding: "14px 16px",
                backgroundColor: "#1e3a3a",
                border: "1px solid #10b981",
                borderRadius: 8,
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "rgba(16, 185, 129, 0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ width: 16, height: 16, color: "#34d399" }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>Just Book Time</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                  Quantity will be calculated automatically when synced
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Quantity Input */}
        {mode === "quantity" && (
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                color: "#a1a1aa",
                marginBottom: 8,
              }}
            >
              Pieces Finished
            </label>
            <input
              type="number"
              min="0"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              autoFocus
              placeholder="Enter quantity..."
              style={{
                width: "100%",
                padding: "12px 14px",
                fontSize: 16,
                backgroundColor: "#111",
                color: "#fff",
                border: "1px solid #3b82f6",
                borderRadius: 8,
                outline: "none",
              }}
            />
            <button
              onClick={() => setMode(null)}
              style={{
                marginTop: 8,
                background: "none",
                border: "none",
                color: "#71717a",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              ← Back to options
            </button>
          </div>
        )}

        {/* Auto Mode Confirmation */}
        {mode === "auto" && (
          <div
            style={{
              padding: 12,
              backgroundColor: "rgba(16, 185, 129, 0.1)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              borderRadius: 8,
              marginBottom: 20,
              fontSize: 13,
              color: "#6ee7b7",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Auto-Quantity Mode</div>
            When this ticket syncs to JobBOSS, the quantity will be calculated based on the
            operation type and job routing.
            <button
              onClick={() => setMode(null)}
              style={{
                display: "block",
                marginTop: 8,
                background: "none",
                border: "none",
                color: "#71717a",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              ← Back to options
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: "10px 20px",
              fontSize: 14,
              backgroundColor: "#333",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          {mode && (
            <button
              onClick={handleComplete}
              disabled={submitting || (mode === "quantity" && !quantity)}
              style={{
                padding: "10px 20px",
                fontSize: 14,
                backgroundColor:
                  submitting || (mode === "quantity" && !quantity)
                    ? "#444"
                    : "#f59e0b",
                color: "#000",
                border: "none",
                borderRadius: 8,
                fontWeight: 600,
                cursor:
                  submitting || (mode === "quantity" && !quantity)
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {submitting ? "Completing..." : "Complete Task"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
